/**
 * queryDispatcher.js
 *
 * Phase 7B — the explicit execution dispatcher approved in the Phase 7B
 * Design Gate. This does NOT unify the four Adapters' signatures into a
 * fake common interface — executeQuery(), executeTimeSeriesQuery(), and
 * executeMonthlyActualCostQuery() keep their own real signatures. This
 * module only selects which one to call, and resolves each dataset's
 * own temporal input (or lack thereof) before calling it — visible
 * branching, not a disguised abstraction.
 */

import { executeQuery } from './financialSSOTAdapter'
import { executeHistoricalQuery } from './historicalSSOTAdapter'
import { executeTimeSeriesQuery } from './timeSeriesSSOTAdapter'
import { executeMonthlyActualCostQuery } from './monthlyActualCostSSOTAdapter'
import { resolveAllAvailableSnapshots } from './temporalResolver'
import { executeCompositeOutput, COMPOSITE_OUTPUTS } from './compositeOutputs'

/**
 * @param {string} projectId
 * @param {{query: AnalyticalQuery, temporalInput?}} analyticalDefinition
 * @param {string} [snapshotId] - required only for historical_comparison
 *   (a specific committed snapshot, not resolved here — Phase 3's
 *   historicalSSOTAdapter takes a snapshotId directly, not a date range;
 *   the caller supplies which snapshot, same as every existing Test
 *   Harness for this dataset already does)
 */
export async function executeAnalyticalDefinition(projectId, analyticalDefinition, snapshotId = null) {
  const { query, temporalInput } = analyticalDefinition
  const dataset = query.dataset

  if (dataset === 'cost_performance') {
    return executeQuery(projectId, query)
  }

  if (dataset === 'historical_comparison') {
    if (!snapshotId) {
      throw new Error('historical_comparison requires a snapshotId — none was provided.')
    }
    return executeHistoricalQuery(snapshotId, query)
  }

  if (dataset === 'time_series') {
    if (!temporalInput || temporalInput.mode !== 'all_available') {
      throw new Error(
        `time_series requires temporalInput.mode === 'all_available' — got ${JSON.stringify(temporalInput)}`
      )
    }
    const forecastDates = await resolveAllAvailableSnapshots(projectId)
    return executeTimeSeriesQuery(projectId, forecastDates, query)
  }

  if (dataset === 'monthly_actual_cost') {
    return executeMonthlyActualCostQuery(projectId, null, query)
  }

  throw new Error(`executeAnalyticalDefinition: unknown dataset "${dataset}"`)
}

/**
 * Stage 4 — the single entry point the Report Builder UI calls, which
 * distinguishes a regular Dataset (query-based, executeAnalyticalDefinition
 * above) from a Composite Output (S-Curve, no AnalyticalQuery shape at
 * all). Kept as a SEPARATE function rather than folded into
 * executeAnalyticalDefinition() — S-Curve has no `query`/`dimension`/
 * `measures` to validate or dispatch on, so merging the two would mean
 * either faking a query shape for S-Curve (rejected — same "no
 * disguised abstraction" rule this file's header already states) or
 * adding S-Curve-specific branches inside a function whose whole
 * contract is "takes an AnalyticalQuery".
 *
 * IMPORTANT: `analyticalDefinition` here is the SAME object shape
 * savedReportsService.js persists/returns as analytical_definition —
 * { outputType, query?, temporalInput? } for a dataset, or
 * { outputType, compositeKey } for a composite. There is NO extra
 * nesting level (no analyticalDefinition.analyticalDefinition) — a
 * mismatch between this function's expected shape and
 * ReportEditor.jsx's draftAnalyticalDefinition construction previously
 * caused a "Cannot destructure property 'query' of 'analyticalDefinition'
 * as it is undefined" runtime error; fixed by aligning both to the one
 * true shape (the persisted analytical_definition's own shape) rather
 * than inventing a second, wrapper shape for this function alone.
 *
 * @param {string} projectId
 * @param {{outputType: 'dataset'|'composite', query?, temporalInput?, compositeKey?}} analyticalDefinition
 * @param {string} [snapshotId] - passed through for historical_comparison
 */
export async function executeAnalyticalOutput(projectId, analyticalDefinition, snapshotId = null) {
  if (!analyticalDefinition || typeof analyticalDefinition !== 'object') {
    throw new Error('executeAnalyticalOutput: analyticalDefinition must be an object')
  }
  if (analyticalDefinition.outputType === 'composite') {
    if (!COMPOSITE_OUTPUTS[analyticalDefinition.compositeKey]) {
      throw new Error(`Unknown composite output: "${analyticalDefinition.compositeKey}"`)
    }
    return executeCompositeOutput(projectId, analyticalDefinition.compositeKey)
  }
  if (analyticalDefinition.outputType === 'dataset') {
    return executeAnalyticalDefinition(projectId, analyticalDefinition, snapshotId)
  }
  throw new Error(`executeAnalyticalOutput: outputType must be 'dataset' or 'composite', got "${analyticalDefinition.outputType}"`)
}
